/* Generatoren und Grundformen des Mapeditors: reine Funktionen, liefern Brushes bzw. Eckpunkte. */
import type { Brush, Vec3 } from './types.ts';
import Core from '../core.ts';
type Pt2 = [number, number];
export type ShapeKind = 'box' | 'wedge' | 'pyramid' | 'cylinder';
export type ArchStyle = 'round' | 'pointed' | 'flat' | 'gable' | 'cone' | 'onion';
/** Textur je Richtung: Oberseite, Unterseite, Seiten */
export interface TexMap { top?: string; bottom?: string; side?: string }
export interface ShapeOpts { sides?: number; tex?: string }
export interface RoomParams {
  x0: number; y0: number; z0: number; x1: number; y1: number; z1: number;
  wall: number; floor: number; ceil: number; texWall: string; texFloor: string; texCeil: string;
  door?: { side: string; width: number; height: number; offset?: number };
}
export interface StairsParams { x0: number; y0: number; z0: number; width: number; steps: number; rise: number; run: number; dir: string; tex: string; texTop?: string }
export interface CliffParams {
  x0: number; y0: number; z0: number; x1: number; y1: number; z1: number;
  facing: string; cell: number; ratio: number; rough: number; fine: number; wave: number; lean: number; seed?: number; tex: string;
}
export interface MoundParams { cx: number; cy: number; z0: number; radius: number; height: number; step?: number; seed?: number; tex: string; texBottom?: string }
/** Sichtlinie (Hint): Platte quer zur Achse an der Stelle pos, über die Öffnung a0..a1 (quer) und z0..z1 (Höhe), dazu etwas Rahmen */
export interface HintParams { axis: string; pos: number; a0: number; a1: number; z0: number; z1: number; thick?: number; margin?: number }
export interface PlaneParams { x0: number; y0: number; x1: number; y1: number; z0: number; thick?: number; step?: number; tex: string; texBottom?: string }
export interface TerrainParams { x0: number; y0: number; x1: number; y1: number; z0: number; height: number; step?: number; seed?: number; basin?: number; tex: string; texBottom?: string }
export interface ArchParams {
  x0: number; y0: number; z0: number; axis?: string; style?: ArchStyle | string; W: number; H: number; T: number; D: number; seg?: number;
  mode?: string; WW: number; WH: number; tex: string; texArch?: string; grid?: number;
}
export interface RingParams { cx: number; cy: number; z0: number; rIn: number; T: number; H: number; start: number; sweep: number; seg: number; taper?: number; tex: string; texIn?: string; grid?: number }
export interface DomeParams { cx: number; cy: number; z0: number; R: number; H: number; T: number; style?: ArchStyle | string; sec: number; rings?: number; start?: number; tex: string; texIn?: string; grid?: number }

const Gens = (function (Core) {
  'use strict';

  function rng(seed: number): () => number {
    let a = (seed | 0) >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function makeNoise(seed: number): (u: number, v: number, spacing: number, lvl: number) => number {
    const r = rng(seed), cache = new Map<string, number>();
    const vn = (lvl: number, i: number, j: number): number => {
      const k = lvl + ',' + i + ',' + j;
      if (!cache.has(k)) cache.set(k, r() * 2 - 1);
      return cache.get(k) as number;
    };
    const smooth = (t: number): number => t * t * (3 - 2 * t);
    return function (u: number, v: number, spacing: number, lvl: number): number {
      const fu = u / spacing, fv = v / spacing, iu = Math.floor(fu), iv = Math.floor(fv);
      const tu = smooth(fu - iu), tv = smooth(fv - iv);
      const a = vn(lvl, iu, iv), b = vn(lvl, iu + 1, iv), c = vn(lvl, iu, iv + 1), d = vn(lvl, iu + 1, iv + 1);
      return (a * (1 - tu) + b * tu) * (1 - tv) + (c * (1 - tu) + d * tu) * tv;
    };
  }
  const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));

  // ------------------------------------------------------------ Grundformen
  /** Eckpunkte einer Form in der Box [min,max]. kind: box|wedge|pyramid|cylinder */
  function shapePoints(kind: string, mn: Vec3, mx: Vec3, options?: ShapeOpts): Vec3[] {
    const opts = options || {};
    const [x0, y0, z0] = mn, [x1, y1, z1] = mx;
    if (kind === 'wedge') {
      return [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0], [x0, y1, z1], [x1, y1, z1]];
    }
    if (kind === 'pyramid') {
      return [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0], [Math.round((x0 + x1) / 2), Math.round((y0 + y1) / 2), z1]];
    }
    if (kind === 'cylinder') {
      const n = clamp(opts.sides || 8, 3, 32), pts: Vec3[] = [];
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, rx = (x1 - x0) / 2, ry = (y1 - y0) / 2;
      const off = n % 4 === 0 ? Math.PI / n : 0;
      for (let i = 0; i < n; i++) {
        const a = off + 2 * Math.PI * i / n;
        const x = Math.round(cx + rx * Math.cos(a)), y = Math.round(cy + ry * Math.sin(a));
        pts.push([x, y, z0], [x, y, z1]);
      }
      return pts;
    }
    return [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0], [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]];
  }
  function shapeBrush(kind: string, mn: Vec3, mx: Vec3, opts?: ShapeOpts): Brush | null {
    return Core.makeBrush(shapePoints(kind, mn, mx, opts), { tex: (opts && opts.tex) || 'NULL' });
  }

  /** Textur je Richtung setzen: map = { top, bottom, side } */
  function texByNormal(b: Brush | null, map: TexMap): Brush | null {
    if (!b) return b;
    b.faces = b.faces.map((f) => {
      const n = Core.faceNormal(b.verts, f.vi);
      const t = n[2] > 0.7 ? map.top : n[2] < -0.7 ? map.bottom : map.side;
      return t ? Object.assign({}, f, { tex: t }) : f;
    });
    return b;
  }
  function boxB(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, map: TexMap): Brush | null {
    if (!(x1 > x0 && y1 > y0 && z1 > z0)) return null;
    const b = Core.makeBrush(shapePoints('box', [x0, y0, z0], [x1, y1, z1]), { tex: map.side });
    return texByNormal(b, map);
  }

  // ------------------------------------------------------------ Raum
  function subtract(iv: Pt2[], cut: Pt2): Pt2[] {
    const res: Pt2[] = [];
    iv.forEach((s) => {
      if (cut[1] <= s[0] || cut[0] >= s[1]) { res.push(s); return; }
      if (cut[0] > s[0]) res.push([s[0], cut[0]]);
      if (cut[1] < s[1]) res.push([cut[1], s[1]]);
    });
    return res;
  }
  /** Wandsegmente entlang axis ('x'|'y') von a0 bis a1, Dicke c0..c1, mit Aussparungen [u0,u1,zb,zt]. */
  function wallBoxes(axis: string, c0: number, c1: number, a0: number, a1: number, z0: number, z1: number, cuts: number[][]): number[][] {
    const set = new Set([a0, a1]);
    cuts.forEach((c) => { set.add(clamp(c[0], a0, a1)); set.add(clamp(c[1], a0, a1)); });
    const us = Array.from(set).sort((a, b) => a - b), segs: { u0: number; u1: number; iv: Pt2[]; key: string }[] = [];
    for (let i = 0; i < us.length - 1; i++) {
      let iv: Pt2[] = [[z0, z1]];
      cuts.forEach((c) => { if (c[0] <= us[i] && c[1] >= us[i + 1]) iv = subtract(iv, [c[2], c[3]]); });
      const key = JSON.stringify(iv), last = segs[segs.length - 1];
      if (last && last.key === key && last.u1 === us[i]) last.u1 = us[i + 1];
      else segs.push({ u0: us[i], u1: us[i + 1], iv: iv, key: key });
    }
    const out: number[][] = [];
    segs.forEach((s) => s.iv.forEach((z) => {
      if (z[1] > z[0] && s.u1 > s.u0) out.push(axis === 'x' ? [s.u0, c0, z[0], s.u1, c1, z[1]] : [c0, s.u0, z[0], c1, s.u1, z[1]]);
    }));
    return out;
  }

  /** p: x0..z1 (Außenmaße), wall, floor, ceil, texWall, texFloor, texCeil, door {side,width,height,offset} */
  function room(p: RoomParams): Brush[] {
    const out: Brush[] = [], w = p.wall, zb = p.z0 + p.floor, zt = p.z1 - p.ceil;
    const T: TexMap = { side: p.texWall };
    const push = (b: Brush | null): void => { if (b) out.push(b); };
    push(boxB(p.x0, p.y0, p.z0, p.x1, p.y1, zb, { side: p.texWall, top: p.texFloor, bottom: p.texWall }));
    push(boxB(p.x0, p.y0, zt, p.x1, p.y1, p.z1, { side: p.texWall, top: p.texWall, bottom: p.texCeil }));
    const d: { side: string; width?: number; height?: number; offset?: number } = p.door || { side: 'none' };
    const cutsFor = (side: string, mid: number): number[][] => {
      if (d.side !== side) return [];
      const c = mid + (d.offset || 0), hw = (d.width as number) / 2;
      return [[c - hw, c + hw, zb, zb + (d.height as number)]];
    };
    const walls = [
      wallBoxes('y', p.x0, p.x0 + w, p.y0, p.y1, zb, zt, cutsFor('-x', (p.y0 + p.y1) / 2)),
      wallBoxes('y', p.x1 - w, p.x1, p.y0, p.y1, zb, zt, cutsFor('+x', (p.y0 + p.y1) / 2)),
      wallBoxes('x', p.y0, p.y0 + w, p.x0 + w, p.x1 - w, zb, zt, cutsFor('-y', (p.x0 + p.x1) / 2)),
      wallBoxes('x', p.y1 - w, p.y1, p.x0 + w, p.x1 - w, zb, zt, cutsFor('+y', (p.x0 + p.x1) / 2))
    ];
    walls.forEach((list) => list.forEach((b) => push(boxB(b[0], b[1], b[2], b[3], b[4], b[5], T))));
    return out;
  }

  // ------------------------------------------------------------ Treppe
  /** p: x0,y0,z0 (Startecke unten), width, steps, rise, run, dir ('+x','-x','+y','-y'), tex */
  function stairs(p: StairsParams): Brush[] {
    const out: Brush[] = [], sign = p.dir[0] === '-' ? -1 : 1, alongX = p.dir[1] === 'x';
    for (let k = 0; k < p.steps; k++) {
      const a0 = k * p.run * sign, a1 = (k + 1) * p.run * sign, lo = Math.min(a0, a1), hi = Math.max(a0, a1);
      const z1 = p.z0 + p.rise * (k + 1);
      const b = alongX
        ? boxB(p.x0 + lo, p.y0, p.z0, p.x0 + hi, p.y0 + p.width, z1, { side: p.tex, top: p.texTop || p.tex })
        : boxB(p.x0, p.y0 + lo, p.z0, p.x0 + p.width, p.y0 + hi, z1, { side: p.tex, top: p.texTop || p.tex });
      if (b) out.push(b);
    }
    return out;
  }

  // ------------------------------------------------------------ Felswand
  /** p: x0..z1 Region, facing ('-x'|'+x'|'-y'|'+y'), cell, ratio (Anteil der Tiefe), rough, fine, wave, lean, seed, tex */
  function cliff(p: CliffParams): Brush[] {
    const sgn = p.facing[0], ax = p.facing[1];
    const noise = makeNoise(p.seed || 1);
    const u0 = ax === 'x' ? p.y0 : p.x0, u1 = ax === 'x' ? p.y1 : p.x1;
    const d0 = ax === 'x' ? p.x0 : p.y0, d1 = ax === 'x' ? p.x1 : p.y1;
    const depth = d1 - d0, back = sgn === '-' ? d1 : d0;
    const nu = Math.max(1, Math.round((u1 - u0) / p.cell)), nv = Math.max(1, Math.round((p.z1 - p.z0) / p.cell));
    const W = (u: number, v: number, dc: number): Vec3 => (ax === 'x' ? [dc, u, v] : [u, dc, v]);
    const thick = (u: number, v: number): number => {
      const t = depth * p.ratio + p.wave * Math.sin(u / 290) + p.rough * noise(u, v, 320, 0) + p.rough * 0.5 * noise(u, v, 160, 1)
        + p.fine * noise(u, v, 64, 2) + p.lean * (v - p.z0) + (p.rough > 0 ? 0.2 * p.fine * Math.sin(v / 38) : 0);
      return clamp(Math.round(t), 8, depth - 8);
    };
    const V: { u: number; v: number; f: number }[][] = [];
    for (let i = 0; i <= nu; i++) {
      V[i] = [];
      for (let j = 0; j <= nv; j++) {
        const u = Math.round(u0 + (u1 - u0) * i / nu), v = Math.round(p.z0 + (p.z1 - p.z0) * j / nv);
        const t = thick(u, v), front = sgn === '-' ? back - t : back + t;
        V[i][j] = { u: u, v: v, f: front };
      }
    }
    const out: Brush[] = [];
    for (let i = 0; i < nu; i++) for (let j = 0; j < nv; j++) {
      const a = V[i][j], b = V[i + 1][j], c = V[i + 1][j + 1], d = V[i][j + 1];
      const tris = (i + j) % 2 === 0 ? [[a, b, c], [a, c, d]] : [[a, b, d], [b, c, d]];
      tris.forEach((t) => {
        const pts = t.map((q) => W(q.u, q.v, q.f)).concat(t.map((q) => W(q.u, q.v, back)));
        const br = Core.makeBrush(pts, { tex: p.tex });
        if (br) {
          br.faces = br.faces.map((f) => Object.assign({}, f, { xs: 0.5, ys: 0.5 }));
          out.push(br);
        }
      });
    }
    return out;
  }

  // ------------------------------------------------------------ Höhenraster → Dreiecks-Prismen
  /** Gemeinsame Schleife von mound und terrain: Raster H[i][j] (Eckpunkte) wird je Zelle zu zwei Dreiecks-Prismen bis zur Ebene base, Diagonale wechselt je Zelle.
   *  Oberseite und Seiten tragen tex; nur die Unterseite bekommt texBottom (Standard: tex). Seiten bleiben sichtbar, weil Nachbarn und Ränder sie freilegen. Dreiecke, deren höchster Punkt skipAtOrBelow nicht übersteigt, entfallen. */
  function gridPrisms(H: Vec3[][], base: number, tex: string, texBottom?: string, skipAtOrBelow?: number): Brush[] {
    const out: Brush[] = [], nx = H.length - 1, ny = H[0].length - 1;
    for (let i = 0; i < nx; i++) for (let j = 0; j < ny; j++) {
      const a = H[i][j], b = H[i + 1][j], c = H[i + 1][j + 1], d = H[i][j + 1];
      const tris = (i + j) % 2 === 0 ? [[a, b, c], [a, c, d]] : [[a, b, d], [b, c, d]];
      tris.forEach((t) => {
        if (skipAtOrBelow !== undefined && Math.max(t[0][2], t[1][2], t[2][2]) <= skipAtOrBelow) return;
        const br = Core.makeBrush(t.concat(t.map((q) => [q[0], q[1], base] as Vec3)), { tex });
        if (!br) return;
        br.faces = br.faces.map((f) => {
          const down = Core.faceNormal(br.verts, f.vi)[2] < -0.7;
          return Object.assign({}, f, { xs: 0.5, ys: 0.5 }, down && texBottom ? { tex: texBottom } : {});
        });
        out.push(br);
      });
    }
    return out;
  }

  // ------------------------------------------------------------ Geröllhügel
  /** p: cx, cy, z0 (Bodenhöhe), radius, height, step, seed, tex, texBottom (optional, nur Unterseite, Standard tex) */
  function mound(p: MoundParams): Brush[] {
    const noise = makeNoise(p.seed || 1), step = p.step || 48, rad = Math.round(p.radius / step) * step;
    const n = (2 * rad) / step, H: Vec3[][] = [];
    for (let i = 0; i <= n; i++) {
      H[i] = [];
      for (let j = 0; j <= n; j++) {
        const x = p.cx - rad + i * step, y = p.cy - rad + j * step;
        const fall = Math.max(0, 1 - Math.hypot(x - p.cx, y - p.cy) / rad);
        const h = p.height * Math.pow(fall, 1.1) + (fall > 0 ? (p.height * 0.25 * noise(x, y, step * 3, 0) + p.height * 0.12 * noise(x, y, step, 1)) * Math.min(1, fall * 3) : 0) - 4;
        H[i][j] = [x, y, Math.round(p.z0 + h)];
      }
    }
    return gridPrisms(H, p.z0 - 12, p.tex, p.texBottom, p.z0);
  }

  // ------------------------------------------------------------ Hint-Brush
  /** Hint-Brush: dünne Platte, HINT nur auf der +Fläche der Achse, SKIP auf allen anderen (andere Texturen bricht csg ab). axis 'x'|'y' = Richtung der Plattennormalen */
  function hint(p: HintParams): Brush | null {
    const t = Math.max(1, p.thick || 8), m = p.margin === undefined ? 16 : p.margin, ax = p.axis === 'y' ? 1 : 0;
    const lo = p.pos - t / 2, hi = p.pos + t / 2, a0 = p.a0 - m, a1 = p.a1 + m;
    const mn: Vec3 = ax === 0 ? [lo, a0, p.z0] : [a0, lo, p.z0], mx: Vec3 = ax === 0 ? [hi, a1, p.z1] : [a1, hi, p.z1];
    const b = Core.makeBrush(shapePoints('box', mn, mx), { tex: 'SKIP' });
    if (!b) return b;
    b.faces = b.faces.map((f) => (Core.faceNormal(b.verts, f.vi)[ax] > 0.9 ? Object.assign({}, f, { tex: 'HINT' }) : f));
    return b;
  }

  // ------------------------------------------------------------ Gelände
  /** Rechteckiges Gelände aus einem Höhenraster (Hügel, Mulden, Becken), nach dem Vorbild der Terrain-Anleitung.
   *  p: x0,y0,x1,y1, z0 (Grundhöhe), height (Hügelhöhe), step, seed, basin (Tiefe der Mulde in der Mitte, 0 = keine), tex (Oberseite), texBottom (nur Unterseite, Standard NULL) */
  function terrain(p: TerrainParams): Brush[] {
    const noise = makeNoise(p.seed || 1), step = Math.max(16, p.step || 64);
    const nx = Math.max(1, Math.round((p.x1 - p.x0) / step)), ny = Math.max(1, Math.round((p.y1 - p.y0) / step));
    const cx = (p.x0 + p.x1) / 2, cy = (p.y0 + p.y1) / 2, rb = Math.min(p.x1 - p.x0, p.y1 - p.y0) * 0.3, basin = p.basin || 0;
    const H: Vec3[][] = []; let low = Infinity;
    for (let i = 0; i <= nx; i++) {
      H[i] = [];
      for (let j = 0; j <= ny; j++) {
        const x = Math.round(p.x0 + (p.x1 - p.x0) * i / nx), y = Math.round(p.y0 + (p.y1 - p.y0) * j / ny);
        const fall = basin > 0 ? Math.max(0, 1 - Math.hypot(x - cx, y - cy) / rb) : 0;
        const h = p.height * (0.5 * noise(x, y, step * 4, 0) + 0.25 * noise(x, y, step * 2, 1) + 0.1 * noise(x, y, step, 2)) - basin * fall * fall * (3 - 2 * fall);
        const z = Math.round(p.z0 + h);
        low = Math.min(low, z);
        H[i][j] = [x, y, z];
      }
    }
    return gridPrisms(H, low - 16, p.tex, p.texBottom || 'NULL');
  }

  // ------------------------------------------------------------ Ebene Dreiecksfläche zum Formen
  /** Ebene Fläche aus Dreiecks-Prismen als Rohling für das Werkzeug „Gelände formen“. z0 = Oberfläche, thick = Dicke darunter (Platz zum Eindrücken),
   *  step = Rasterweite. Eckpunkte liegen auf ganzen Zahlen und werden von Nachbarn geteilt (keine Spalten, jede Oberseite ist ein ebenes Dreieck). */
  function plane(p: PlaneParams): Brush[] {
    const step = Math.max(16, p.step || 64), thick = Math.max(16, p.thick || 128);
    const nx = Math.max(1, Math.round((p.x1 - p.x0) / step)), ny = Math.max(1, Math.round((p.y1 - p.y0) / step));
    const H: Vec3[][] = [];
    for (let i = 0; i <= nx; i++) {
      H[i] = [];
      for (let j = 0; j <= ny; j++) H[i][j] = [Math.round(p.x0 + (p.x1 - p.x0) * i / nx), Math.round(p.y0 + (p.y1 - p.y0) * j / ny), Math.round(p.z0)];
    }
    return gridPrisms(H, Math.round(p.z0) - thick, p.tex, p.texBottom || 'NULL');
  }

  // ------------------------------------------------------------ Bögen, Rundungen, Kuppeln
  const rnd = (x: number, g: number): number => (g > 0 ? Math.round(x / g) * g : Math.round(x * 100) / 100);
  const hyp = Math.hypot;

  /** Halbprofil von (a,0) nach (0,rise). style: round | pointed | flat | gable | cone | onion. */
  function profileHalf(style: string, a: number, riseIn: number, nIn: number): Pt2[] {
    const n = Math.max(1, Math.round(nIn));
    let rise = riseIn;
    const pts: Pt2[] = [];
    if (style === 'gable' || style === 'cone') return [[a, 0], [0, rise]];
    if (style === 'onion') {
      const f = (t: number): number => Math.pow(1 - t, 0.6) * (0.78 + 0.35 * Math.sin(Math.PI * Math.pow(t, 0.8)));
      let mx = 0; for (let i = 0; i <= 200; i++) mx = Math.max(mx, f(i / 200));
      const m = Math.max(n, 6);
      for (let i = 0; i <= m; i++) { const t = i / m; pts.push([i === m ? 0 : a * f(t) / mx, rise * t]); }
      return pts;
    }
    if (style === 'pointed') {
      rise = Math.max(rise, a * 1.0001);
      const R = (rise * rise + a * a) / (2 * a), tm = Math.acos((R - a) / R);
      for (let i = 0; i <= n; i++) { const t = tm * i / n; pts.push([i === n ? 0 : a - R + R * Math.cos(t), i === n ? rise : R * Math.sin(t)]); }
      return pts;
    }
    if (style === 'flat') {
      rise = Math.min(rise, a * 0.9999);
      const R = (a * a + rise * rise) / (2 * rise), cz = rise - R, f0 = Math.asin((R - rise) / R);
      for (let i = 0; i <= n; i++) { const t = f0 + (Math.PI / 2 - f0) * i / n; pts.push([i === n ? 0 : R * Math.cos(t), i === n ? rise : cz + R * Math.sin(t)]); }
      pts[0] = [a, 0];
      return pts;
    }
    for (let i = 0; i <= n; i++) { const t = Math.PI / 2 * i / n; pts.push([i === n ? 0 : a * Math.cos(t), i === 0 ? 0 : rise * Math.sin(t)]); }
    return pts;
  }
  /** Linienzug seitlich versetzen (Gehrung). nf(dx,dh) liefert die Normale, T>0 in Normalenrichtung. Endpunkte: waagerecht/senkrecht nach endN. */
  function offsetPoly(poly: Pt2[], T: number, nf: (dx: number, dh: number) => Pt2, endN?: (Pt2 | null)[]): Pt2[] {
    const seg: Pt2[] = [];
    for (let i = 0; i < poly.length - 1; i++) {
      const dx = poly[i + 1][0] - poly[i][0], dh = poly[i + 1][1] - poly[i][1], l = hyp(dx, dh) || 1;
      seg.push(nf(dx / l, dh / l));
    }
    return poly.map((q, i): Pt2 => {
      let n: Pt2, sc = 1;
      if (i === 0) n = endN && endN[0] ? endN[0] : seg[0];
      else if (i === poly.length - 1) n = endN && endN[1] ? endN[1] : seg[seg.length - 1];
      else {
        const na = seg[i - 1], nb = seg[i], m = [na[0] + nb[0], na[1] + nb[1]], l = hyp(m[0], m[1]) || 1;
        n = [m[0] / l, m[1] / l]; sc = 1 / Math.max(0.35, n[0] * na[0] + n[1] * na[1]);
      }
      return [q[0] + n[0] * T * sc, q[1] + n[1] * T * sc];
    });
  }
  /** Flächen anhand der Eckpunktmenge texturieren: liegen alle Punkte in innerSet, bekommt die Fläche texIn. */
  function texInner(b: Brush | null, innerSet: Set<string> | null, texIn: string | undefined, key: (v: Vec3) => string): Brush | null {
    if (!b || !texIn || !innerSet) return b;
    b.faces = b.faces.map((f) => (f.vi.every((i) => innerSet.has(key(b.verts[i]))) ? Object.assign({}, f, { tex: texIn }) : f));
    return b;
  }
  const keyOf = (v: Vec3): string => v[0].toFixed(2) + ',' + v[1].toFixed(2) + ',' + v[2].toFixed(2);

  /** Bogen. p: x0,y0,z0 (Mitte Basis), axis 'x'|'y' (Richtung der Öffnungsbreite), style, W, H, T, D, seg, mode 'free'|'wall', WW, WH, tex, texArch, grid */
  function arch(p: ArchParams): Brush[] {
    const g = p.grid === undefined ? 1 : p.grid, a = p.W / 2, ax = p.axis || 'x';
    const half = profileHalf(p.style || 'round', a, p.H, p.seg || 8);
    const full = half.map((q): Pt2 => [-q[0], q[1]]).concat(half.slice(0, -1).reverse());
    const T = p.T, out: Brush[] = [];
    const d0 = -Math.round(p.D / 2), d1 = d0 + p.D;
    const X = (u: number, d: number, h: number): Vec3 => (ax === 'x' ? [p.x0 + u, p.y0 + d, p.z0 + h] : [p.x0 + d, p.y0 + u, p.z0 + h]);
    const prism = (poly2: Pt2[], tex: string): Brush | null => {
      const pts: Vec3[] = [];
      poly2.forEach((q) => { [d0, d1].forEach((d) => { const v = X(q[0], d, q[1]); pts.push([rnd(v[0], g), rnd(v[1], g), rnd(v[2], g)]); }); });
      const b = Core.makeBrush(pts, { tex: tex });
      return b;
    };
    const nf = (dx: number, dh: number): Pt2 => [-dh, dx];
    const endN: Pt2[] = [[-1, 0], [1, 0]];
    const outer = T > 0 ? offsetPoly(full, T, nf, endN) : full;
    if (T > 0) for (let i = 0; i < full.length - 1; i++) {
      const b = prism([full[i], full[i + 1], outer[i + 1], outer[i]], p.texArch || p.tex);
      if (b) out.push(b);
    }
    if (p.mode === 'wall') {
      let top = p.WH;
      const mh = Math.max.apply(null, outer.map((q) => q[1]));
      top = Math.max(top, mh + 8);
      for (let i = 0; i < outer.length - 1; i++) {
        const A = outer[i], B = outer[i + 1];
        if (Math.abs(A[0] - B[0]) < 0.5) continue;
        const b = prism([A, B, [A[0], top], [B[0], top]], p.tex);
        if (b) out.push(b);
      }
      const e0 = outer[0][0], e1 = outer[outer.length - 1][0], ww = p.WW / 2;
      if (ww > Math.abs(e0) + 0.5) {
        const bl = prism([[-ww, 0], [e0, 0], [-ww, top], [e0, top]], p.tex), br = prism([[e1, 0], [ww, 0], [e1, top], [ww, top]], p.tex);
        if (bl) out.push(bl); if (br) out.push(br);
      }
    }
    return out;
  }

  /** Rundung/Turm/Säule. p: cx,cy,z0, rIn (0 = voll), T, H, start, sweep (Grad), seg, taper, tex, texIn, grid */
  function ring(p: RingParams): Brush[] {
    const g = p.grid === undefined ? 1 : p.grid, out: Brush[] = [], n = Math.max(1, Math.round(p.seg));
    const rO = p.rIn + p.T, tp = p.taper || 0;
    const P = (a: number, r: number, z: number): Vec3 => { const t = a * Math.PI / 180; return [rnd(p.cx + r * Math.cos(t), g), rnd(p.cy + r * Math.sin(t), g), rnd(p.z0 + z, g)]; };
    for (let k = 0; k < n; k++) {
      const a0 = p.start + p.sweep * k / n, a1 = p.start + p.sweep * (k + 1) / n, pts: Vec3[] = [];
      const rt = (r: number): number => Math.max(0, r + tp);
      [a0, a1].forEach((a) => {
        pts.push(P(a, rO, 0), P(a, rt(rO), p.H));
        if (p.rIn > 0) pts.push(P(a, p.rIn, 0), P(a, rt(p.rIn), p.H));
      });
      if (!(p.rIn > 0)) pts.push(P(0, 0, 0), P(0, 0, p.H));
      const b = Core.makeBrush(pts, { tex: p.tex });
      if (b) {
        if (p.rIn > 0 && p.texIn) {
          const inner = new Set<string>();
          [a0, a1].forEach((a) => { inner.add(keyOf(P(a, p.rIn, 0))); inner.add(keyOf(P(a, rt(p.rIn), p.H))); });
          texInner(b, inner, p.texIn, keyOf);
        }
        out.push(b);
      }
    }
    return out;
  }

  /** Kuppel/Turmhelm aus Drehkörper. p: cx,cy,z0, R, H, T (0 = massiv), style, sec, rings, start, tex, texIn, grid */
  function dome(p: DomeParams): Brush[] {
    const g = p.grid === undefined ? 1 : p.grid, out: Brush[] = [], sec = Math.max(3, Math.round(p.sec));
    const outer = profileHalf(p.style || 'round', p.R, p.H, p.rings || 6);
    const nf = (dx: number, dh: number): Pt2 => [dh, -dx];
    const solid = !(p.T > 0);
    const inner: Pt2[] | null = solid ? null : offsetPoly(outer, -p.T, nf, [[1, 0], [0, 1]]).map((q): Pt2 => [Math.max(0, q[0]), q[1]]);
    const P = (a: number, q: Pt2): Vec3 => { const t = a * Math.PI / 180; return [rnd(p.cx + q[0] * Math.cos(t), g), rnd(p.cy + q[0] * Math.sin(t), g), rnd(p.z0 + q[1], g)]; };
    for (let k = 0; k < sec; k++) {
      const a0 = (p.start || 0) + 360 * k / sec, a1 = (p.start || 0) + 360 * (k + 1) / sec;
      for (let i = 0; i < outer.length - 1; i++) {
        const pts: Vec3[] = [], innerKeys = new Set<string>();
        [a0, a1].forEach((a) => {
          pts.push(P(a, outer[i]), P(a, outer[i + 1]));
          if (!solid) { const A = P(a, (inner as Pt2[])[i]), B = P(a, (inner as Pt2[])[i + 1]); pts.push(A, B); innerKeys.add(keyOf(A)); innerKeys.add(keyOf(B)); }
        });
        if (solid) pts.push([rnd(p.cx, g), rnd(p.cy, g), rnd(p.z0, g)]);
        const b = Core.makeBrush(pts, { tex: p.tex });
        if (b) { if (!solid) texInner(b, innerKeys, p.texIn, keyOf); out.push(b); }
      }
    }
    return out;
  }

  return { rng, shapePoints, shapeBrush, texByNormal, room, stairs, cliff, mound, terrain, plane, gridPrisms, hint, profileHalf, offsetPoly, arch, ring, dome };
})(Core);
export default Gens;
